// Battlemap replay: reconstructing the board as it was at any instant.
//
// The battlemap is a snapshot. This module turns the timeline the API
// returns (static structure + an ordered event stream) into a
// LayoutInput AS OF any instant, so the same board can be replayed.
//
// WHY THIS IS CHEAP, AND WHY IT IS PURE
//
// Positions on the board derive from the ticket key ALONE — never from an
// index, a count, or a collection order. So a piece that first appears in
// week 3 appears exactly where it would have been standing all along, and
// replaying requires no layout computation at all. The structure is
// time-invariant; only STATE moves.
//
// Everything here is a pure function of (timeline, instant), with no
// clock, no timezone and no I/O, so it is testable without a browser.
// The one exception is documented at hourBuckets.
//
// THE FOLD
//
// Events are ordered ascending and applied in order. Every reconstructed
// fact is either "the latest event that said something about it" or "the
// set of everything seen so far":
//
//   statusOf     latest transition's target
//   assigneeOf   latest transition that carried an assignee
//   actorsOf     every distinct actor seen so far
//   lastTouchOf  the latest event's instant
//   closedAt     latest transition INTO a terminal status, cleared if the
//                ticket later moved back out
//
// The last one matters: a ticket can be reopened, and a board that kept it
// closed forever would report work as finished that someone had picked
// back up.

import { isRegression } from './workflow';

export interface TimelineEvent {
  t: string;
  key: string;
  kind: 'created' | 'transition' | 'comment' | 'pr';
  to?: string;
  /** Transition source status. Needed to tell a step FORWARDS from a step
   *  BACKWARDS; `to` alone cannot say whether Testing came after Review or
   *  before it. Absent on events recorded before it was captured, and on
   *  every non-transition. */
  from?: string;
  actor?: string | null;
  assignee?: string | null;
}

export interface TimelineOwner {
  id: string;
  handle: string;
  name: string | null;
  color: string | null;
  is_bot: boolean;
}

export interface BattlemapTimeline {
  team_id: string;
  window: { from: string; to: string };
  adventure: { id: string; name: string; starts_at: string; ends_at: string } | null;
  terminal_status_names: string[];
  committed: string[];
  goals: Array<{ id: string; name: string; keys: string[] }>;
  bosses: Array<{ id: string; name: string; hp: number; damage: number }>;
  minion_of: Record<string, string>;
  owners: TimelineOwner[];
  ticket_info: Record<string, { summary: string | null; type: string | null }>;
  /** State at the window's first instant, folded from history before it. */
  baseline: {
    statusOf: Record<string, string>;
    assigneeOf: Record<string, string>;
    actorsOf: Record<string, string[]>;
    lastTouchOf: Record<string, string>;
  };
  events: TimelineEvent[];
}

/** What the fold produces. Every field is "as of the instant asked for". */
export interface ReplayState {
  statusOf: Record<string, string>;
  assigneeOf: Record<string, string>;
  actorsOf: Record<string, string[]>;
  lastTouchOf: Record<string, string>;
  /** First instant each key was seen at all. A piece cannot stand on the
   *  board before this. */
  appearedAt: Record<string, string>;
  /**
   * First instant each key was MOVED, which is not the same as being seen.
   *
   * This is the difference between queued work and work in flight, and the
   * board is about the latter. A ticket can be created and then sit
   * untouched for a week; it exists, but nobody has started it, and the
   * live board does not draw it either. Measured: the sprint window held
   * nine tickets created and never transitioned, and the replay drew all
   * nine while the live board drew none.
   */
  firstTransitionAt: Record<string, string>;
  /** Set only while the ticket is IN a terminal status. */
  closedAt: Record<string, string>;
}

export function isTerminal(status: string | undefined, terminalStatuses: string[]): boolean {
  if (!status) return false;
  return terminalStatuses.includes(status);
}

/** A timeline event with its timestamp already parsed and the stream
 *  already ordered. */
export interface PreparedEvent {
  ms: number;
  ev: TimelineEvent;
}

/**
 * Parse and sort the stream once, so the fold can walk it and stop early.
 *
 * This exists because of a real failure mode. The fold stops at the first
 * event later than the instant asked for, which is what makes it cheap —
 * but it means an UNSORTED stream silently reconstructs a smaller board
 * and reports success. That bug is invisible: the map just shows less.
 *
 * The API orders by occurred_at ASC, so this is belt and braces. It is
 * here because "the caller sorted it" is an assumption that fails quietly
 * and in the direction of under-reporting.
 *
 * Cached by array identity: the timeline object is fetched once and
 * folded once per frame, so re-parsing 800 timestamps 60 times a second
 * would be the expensive part of the replay.
 */
const preparedCache = new WeakMap<TimelineEvent[], PreparedEvent[]>();

export function prepareEvents(events: TimelineEvent[]): PreparedEvent[] {
  const hit = preparedCache.get(events);
  if (hit) return hit;
  const out: PreparedEvent[] = [];
  for (const ev of events) {
    const ms = Date.parse(ev.t);
    if (!Number.isNaN(ms)) out.push({ ms, ev });
  }
  out.sort((a, b) => a.ms - b.ms);
  preparedCache.set(events, out);
  return out;
}

/**
 * Fold the event stream up to `atMs` (inclusive).
 *
 * Inclusive, not exclusive: when the playhead lands exactly on an event,
 * that event HAS happened. The alternative makes stepping feel one step
 * behind the clock.
 *
 * Terminal statuses are passed in rather than discovered, because
 * discovering them is circular: a status is not terminal because we saw
 * it, it is terminal because the team says so.
 */
export function stateAt(
  events: TimelineEvent[],
  atMs: number,
  terminalStatuses: string[],
  baseline?: BattlemapTimeline['baseline'],
): ReplayState {
  const statusOf: Record<string, string> = {};
  const assigneeOf: Record<string, string> = {};
  const actorsOf: Record<string, string[]> = {};
  const lastTouchOf: Record<string, string> = {};
  const appearedAt: Record<string, string> = {};
  const firstTransitionAt: Record<string, string> = {};
  const closedAt: Record<string, string> = {};
  const seenActors: Record<string, Set<string>> = {};

  // Start from the state the window opened in. A ticket that was already
  // In Progress before the window has had no event inside it yet, so a
  // fold over the window alone would draw it as absent - or, once touched,
  // as though it had just appeared.
  if (baseline) {
    for (const [k, v] of Object.entries(baseline.statusOf)) {
      statusOf[k] = v;
      if (isTerminal(v, terminalStatuses)) closedAt[k] = baseline.lastTouchOf[k] ?? '';
    }
    // Assignee and last-touch carry over: both are facts about NOW. A
    // ticket assigned before the sprint is still somebody's work, and a
    // ticket moved the day before the sprint still moved.
    Object.assign(assigneeOf, baseline.assigneeOf);
    Object.assign(lastTouchOf, baseline.lastTouchOf);
    // ACTORS ARE DELIBERATELY NOT CARRIED OVER.
    //
    // "Who has been involved" is a question about a period, and the board is
    // about this sprint. Seeding it from history put assistance lines on
    // today's board for work somebody had touched in a previous sprint and
    // had not been near since - reported as "links between people and mobs
    // that were before the sprint started".
    //
    // The live board had the same fault through a different route, from an
    // actors query with no time bound at all. Both are now scoped to the
    // sprint; assignee and last-touch remain unbounded on purpose.
  }

  for (const { ms, ev: e } of prepareEvents(events)) {
    if (ms > atMs) break;

    if (!appearedAt[e.key]) appearedAt[e.key] = e.t;
    lastTouchOf[e.key] = e.t;

    if (e.kind === 'transition' && !firstTransitionAt[e.key]) firstTransitionAt[e.key] = e.t;

    if (e.kind === 'transition' && e.to) {
      statusOf[e.key] = e.to;
      if (isTerminal(e.to, terminalStatuses)) closedAt[e.key] = e.t;
      else delete closedAt[e.key];
    }
    if (e.kind === 'transition' && e.assignee) assigneeOf[e.key] = e.assignee;
    if (e.actor) (seenActors[e.key] ??= new Set()).add(e.actor);
  }

  for (const [key, set] of Object.entries(seenActors)) actorsOf[key] = [...set];
  return { statusOf, assigneeOf, actorsOf, lastTouchOf, appearedAt, firstTransitionAt, closedAt };
}

export interface ReplayLayoutInput {
  committed: string[];
  cleared: string[];
  goals: Array<{ id: string; name: string; keys: string[] }>;
  bosses: Array<{ id: string; name: string; hp: number; damage: number }>;
  sideQuests: string[];
  reinforcements: string[];
  statusOf: Record<string, string>;
  ownerOf: Record<string, string>;
  actorsOf: Record<string, string[]>;
  assigneeOf: Record<string, string>;
  minionOf: Record<string, string>;
  lastTouchOf: Record<string, string>;
  startsAt: string;
  endsAt: string;
}

/**
 * The board as of an instant, in the shape layoutBattlefield consumes.
 *
 * The two derived sets are the whole of the replay's structure:
 *
 *   cleared      committed tickets closed at or before this instant
 *   sideQuests   non-committed tickets closed at or before it
 *   reinforcements  non-committed tickets that have APPEARED and are not
 *                   closed — unplanned work that arrived mid-sprint
 *
 * A ticket cannot be in reinforcements and sideQuests at once: closing it
 * moves it from one to the other, which is exactly what the board shows.
 *
 * ownerOf is deliberately left empty. It is the legacy "most recent actor"
 * ownership that the map stopped using when unassigned became unowned;
 * accountability now comes from assigneeOf alone, and inventing an owner
 * here would silently resurrect the fallback the operator rejected.
 */
export function layoutInputAt(timeline: BattlemapTimeline, atMs: number): ReplayLayoutInput {
  const st = stateAt(timeline.events, atMs, timeline.terminal_status_names, timeline.baseline);
  const committedSet = new Set(timeline.committed);

  // A reinforcement ARRIVED during the sprint. The cutoff is the sprint's
  // start, not the window's, and that distinction is not academic: the
  // window can reach back before the current sprint began, and on the live
  // campaign 161 of 172 keys were first seen before the sprint started.
  // Using the window start made 69 still-open tickets from the previous
  // sprint look like unplanned arrivals.
  const sprintStartMs = timeline.adventure
    ? Date.parse(timeline.adventure.starts_at)
    : Date.parse(timeline.window.from);

  // When a piece APPEARS on the board: the first time it was MOVED, not the
  // first time it was seen. A ticket created and left alone has not appeared
  // on the battlefield, because nothing has happened to it.
  const movedAt = (key: string): number => {
    const t = st.firstTransitionAt[key];
    return t ? Date.parse(t) : Number.NaN;
  };

  const cleared: string[] = [];
  const sideQuests: string[] = [];
  const reinforcements: string[] = [];

  // Every key the board knows about: the ones the window touched, plus the
  // ones that were already standing when it opened.
  const known = new Set([
    ...Object.keys(st.appearedAt),
    ...Object.keys(timeline.baseline.statusOf),
  ]);

  for (const key of known) {
    if (committedSet.has(key)) {
      if (st.closedAt[key]) cleared.push(key);
    } else if (st.closedAt[key]) {
      // Same scoping as a reinforcement, for the same reason. The
      // graveyard is "closed work that was never promised" and the live
      // board scopes it to the sprint, so replay has to agree or it
      // reports 78 graves on the sprint's first morning.
      if (Date.parse(st.closedAt[key]!) >= sprintStartMs) sideQuests.push(key);
    } else if (movedAt(key) >= sprintStartMs) {
      // Open, uncommitted, and MOVED this sprint. The move is required:
      // a ticket created and never transitioned is queued, not in flight,
      // and the live board agrees - it requires a transition too. Without
      // this the replay drew nine queued tickets the live board did not.
      reinforcements.push(key);
    }
    // Anything else is open work from an earlier sprint. The live board
    // does not draw it either - it is not this campaign's promise and it
    // is not this campaign's unplanned work.
  }

  // The commitment is a PROMISE, and a promise is standing from the start
  // of the sprint it was made in, not from the first time someone touched
  // it. A committed ticket with no status yet renders as not engaged,
  // which is the honest reading.
  const standingCommitted = timeline.committed.filter(
    (k) => atMs >= sprintStartMs || st.appearedAt[k],
  );

  return {
    committed: standingCommitted,
    cleared,
    goals: timeline.goals,
    bosses: timeline.bosses,
    sideQuests,
    reinforcements,
    statusOf: st.statusOf,
    ownerOf: {},
    actorsOf: st.actorsOf,
    assigneeOf: st.assigneeOf,
    minionOf: timeline.minion_of,
    lastTouchOf: st.lastTouchOf,
    startsAt: timeline.adventure?.starts_at ?? timeline.window.from,
    endsAt: timeline.adventure?.ends_at ?? timeline.window.to,
  };
}

// ---------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------

export interface HourBucket {
  /** Instant the bucket begins at. */
  startMs: number;
  /** Moves + comments + PRs. NOT every event: creations are omitted because
   *  the rail draws the three kinds the board marks, and a total that
   *  included a fourth kind would not equal the height of the stack. */
  count: number;
  /** The three kinds, tracked separately so the rail shows the COMPOSITION
   *  of an hour rather than only its volume. A tall bar that is all
   *  comments is a different day from a tall bar that is all merges. */
  moves: number;
  comments: number;
  prs: number;
  /**
   * Of the moves, how many went BACKWARDS.
   *
   * Not a fourth stack segment: it is a subset of `moves`, not a kind of
   * its own, and stacking it would double-count them. Drawn as a marked
   * tick above the bar instead, because direction is not volume.
   */
  backwards: number;
}

/**
 * One bucket per hour across the window.
 *
 * The caller aligns `fromMs` to a LOCAL hour boundary; that is the only
 * reason the buckets line up with wall-clock hours. The arithmetic here is
 * deliberately elapsed-milliseconds rather than local calendar fields, so
 * this stays a pure function with no timezone dependency.
 *
 * The cost of that choice: across a daylight-saving transition one bucket
 * is an hour out of step in WIDTH. The bucket's START INSTANT is still
 * exact, and the label is rendered from that instant, so what the operator
 * reads is never wrong — only one bar is an hour wider than its
 * neighbours, twice a year. Buying that back needs a timezone-aware
 * calendar walk for no visible gain.
 */
export function hourBuckets(events: TimelineEvent[], fromMs: number, toMs: number): HourBucket[] {
  const HOUR = 3600_000;
  const n = Math.max(1, Math.ceil((toMs - fromMs) / HOUR));
  const buckets: HourBucket[] = Array.from({ length: n }, (_, i) => ({
    startMs: fromMs + i * HOUR,
    count: 0,
    moves: 0,
    comments: 0,
    prs: 0,
    backwards: 0,
  }));

  for (const { ms, ev: e } of prepareEvents(events)) {
    if (ms < fromMs || ms > toMs) continue;
    const i = Math.min(n - 1, Math.floor((ms - fromMs) / HOUR));
    const b = buckets[i]!;
    // Creations are counted in none of the three and so contribute no
    // height. They are not something the board marks: a queued ticket is
    // not on it.
    if (e.kind === 'transition') {
      b.moves += 1;
      b.count += 1;
      if (isRegression(e.from, e.to)) b.backwards += 1;
    }
    else if (e.kind === 'comment') { b.comments += 1; b.count += 1; }
    else if (e.kind === 'pr') { b.prs += 1; b.count += 1; }
  }
  return buckets;
}

/**
 * The instants a "step" lands on: one per event, ascending.
 *
 * Stepping is by EVENT, not by hour, and that is a deliberate choice about
 * this data. Measured on the live campaign, activity clusters into one
 * working block — roughly 47 to 102 events in each of the busy hours and 1
 * to 13 in the rest. Stepping hour by hour would spend about fourteen
 * steps a day crossing a period in which nothing happened.
 *
 * Stepping by event means every step is a thing that happened, and silence
 * is something you SEE on the rail (see quietGaps) rather than something
 * you walk through.
 *
 * Duplicate instants are collapsed: two events in the same millisecond are
 * one step, or the second would appear to do nothing.
 */
export function stepInstants(events: TimelineEvent[]): number[] {
  const out: number[] = [];
  let last = Number.NaN;
  for (const { ms } of prepareEvents(events)) {
    if (ms === last) continue;
    out.push(ms);
    last = ms;
  }
  return out;
}

export interface QuietGap {
  fromMs: number;
  toMs: number;
  hours: number;
}

/**
 * Runs of hours with no events, long enough to be worth naming.
 *
 * These are drawn on the rail as "6h quiet" rather than left as empty
 * space, because on this data the empty space is the interesting part: it
 * is the boundary between one working day and the next.
 *
 * The threshold is in hours and only gaps at or above it are returned, so
 * an ordinary lull inside a working block is not labelled.
 */
export function quietGaps(buckets: HourBucket[], minHours = 3): QuietGap[] {
  const out: QuietGap[] = [];
  let runStart = -1;

  const flush = (endIdx: number) => {
    if (runStart < 0) return;
    const hours = endIdx - runStart;
    if (hours >= minHours) {
      out.push({
        fromMs: buckets[runStart]!.startMs,
        toMs: buckets[endIdx]?.startMs ?? buckets[buckets.length - 1]!.startMs,
        hours,
      });
    }
    runStart = -1;
  };

  for (let i = 0; i < buckets.length; i += 1) {
    if (buckets[i]!.count === 0) {
      if (runStart < 0) runStart = i;
    } else {
      flush(i);
    }
  }
  flush(buckets.length);
  return out;
}

/** Where the playhead lands if you step to the next event after `afterMs`.
 *  Returns null at the end of the window. */
export function nextInstant(instants: number[], afterMs: number): number | null {
  for (const ms of instants) {
    if (ms > afterMs) return ms;
  }
  return null;
}

/** The previous event's instant, or null at the start. Used by the
 *  back-step so it lands ON an event rather than between two. */
export function prevInstant(instants: number[], beforeMs: number): number | null {
  let prev: number | null = null;
  for (const ms of instants) {
    if (ms < beforeMs) prev = ms;
    else break;
  }
  return prev;
}
